import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, ChevronDown, ChevronUp, Search, ArrowRight,
  FileText, DollarSign, Building2, Calculator, Users,
  Shield, Zap, Star, Clock, CheckCircle, MessageCircle
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const FAQPageCompleta = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('todas');

  const navigateTo = (url) => {
    window.location.href = url;
  };

  // Categorias
  const categorias = [
    { id: 'todas', nome: 'Todas', icon: HelpCircle },
    { id: 'abertura', nome: 'Abertura de MEI', icon: Building2 },
    { id: 'custos', nome: 'Custos e Pagamentos', icon: DollarSign },
    { id: 'nf', nome: 'Nota Fiscal', icon: FileText },
    { id: 'obrigacoes', nome: 'Obrigações', icon: Calculator },
    { id: 'beneficios', nome: 'Benefícios', icon: Shield },
  ];

  // FAQs completas
  const faqs = [
    // Abertura de MEI
    {
      categoria: 'abertura',
      pergunta: 'O que é MEI?',
      resposta: 'O MEI (Microempreendedor Individual) é o tipo mais simples de empresa no Brasil. Foi criado para formalizar trabalhadores autônomos e pequenos empreendedores. O MEI pode faturar até R$ 81.000 por ano, ter no máximo 1 funcionário e exercer uma das atividades permitidas. Ao se tornar MEI, você ganha CNPJ, pode emitir notas fiscais e tem acesso a benefícios previdenciários.',
      destaque: true
    },
    {
      categoria: 'abertura',
      pergunta: 'Como abrir um MEI?',
      resposta: 'Abrir um MEI é simples e gratuito! Você pode fazer pelo Portal do Empreendedor (gov.br) ou usar plataformas como o MeuMei que facilitam todo o processo. O passo a passo é: 1) Ter CPF e dados pessoais em mãos; 2) Escolher a atividade (CNAE) que vai exercer; 3) Preencher o cadastro online; 4) Gerar seu CCMEI (Certificado de Condição de MEI). Todo o processo leva menos de 15 minutos!'
    },
    {
      categoria: 'abertura',
      pergunta: 'Quem pode ser MEI?',
      resposta: 'Para ser MEI você precisa: 1) Faturar até R$ 81.000 por ano; 2) Não ser sócio ou titular de outra empresa; 3) Ter no máximo 1 funcionário; 4) Exercer uma das atividades permitidas; 5) Não ser servidor público federal (estadual e municipal varia). Pensionistas e aposentados podem ser MEI, assim como estrangeiros com visto permanente.'
    },
    {
      categoria: 'abertura',
      pergunta: 'Quem não pode ser MEI?',
      resposta: 'Não podem ser MEI: servidores públicos federais, pensionistas e servidores públicos estaduais/municipais de regime próprio (verificar cada caso), menores de 18 anos não emancipados, estrangeiros sem visto permanente, pessoas que já têm outra empresa, e quem exerce atividade não permitida (como médicos, advogados, engenheiros e outras profissões regulamentadas).'
    },
    {
      categoria: 'abertura',
      pergunta: 'Quanto tempo leva para abrir o MEI?',
      resposta: 'A abertura do MEI é imediata! Ao finalizar o cadastro no Portal do Empreendedor ou pelo MeuMei, você já recebe seu CNPJ na hora. O CCMEI (Certificado de Condição de MEI) também é gerado instantaneamente. Em menos de 15 minutos você já está formalizado e pode começar a trabalhar com CNPJ.'
    },
    {
      categoria: 'abertura',
      pergunta: 'Quais atividades são permitidas para MEI?',
      resposta: 'Existem mais de 450 atividades permitidas para MEI! As mais comuns são: cabeleireiro, manicure, eletricista, pintor, pedreiro, vendedor ambulante, costureira, artesão, cozinheiro, motorista de app, fotógrafo, designer, programador freelancer, entre muitas outras. Atividades regulamentadas como medicina, advocacia, engenharia e contabilidade NÃO são permitidas.'
    },

    // Custos e Pagamentos
    {
      categoria: 'custos',
      pergunta: 'Quanto custa ser MEI?',
      resposta: 'O MEI paga uma taxa fixa mensal chamada DAS (Documento de Arrecadação do Simples Nacional). Em 2025, os valores são: Comércio e Indústria: R$ 71,60 (INSS + ICMS), Serviços: R$ 75,60 (INSS + ISS), Comércio e Serviços: R$ 76,60 (INSS + ICMS + ISS). Esse valor único substitui todos os impostos federais, estaduais e municipais!',
      destaque: true
    },
    {
      categoria: 'custos',
      pergunta: 'O que é DAS MEI?',
      resposta: 'DAS é o Documento de Arrecadação do Simples Nacional, a guia de pagamento mensal do MEI. O DAS inclui: 5% do salário mínimo para o INSS, R$ 1,00 de ICMS (comércio/indústria) e R$ 5,00 de ISS (serviços). O vencimento é sempre no dia 20 de cada mês. Com o MeuMei, geramos seu DAS automaticamente e enviamos lembretes!'
    },
    {
      categoria: 'custos',
      pergunta: 'Como pagar o DAS MEI?',
      resposta: 'Você pode pagar o DAS de várias formas: 1) Boleto bancário (em qualquer banco ou lotérica); 2) Débito automático; 3) Pagamento online pelo internet banking; 4) PIX (usando o código de barras); 5) App do banco. Com o MeuMei, você gera o DAS e paga direto pela plataforma ou recebe por e-mail/WhatsApp.'
    },
    {
      categoria: 'custos',
      pergunta: 'O que acontece se eu não pagar o DAS?',
      resposta: 'Se você atrasar o DAS, terá que pagar multa e juros (0,33% ao dia de multa, limitada a 20%, mais juros Selic). Além disso, acumular 2 anos de DAS em atraso pode causar o cancelamento do seu MEI. Também fica impedido de emitir certidões negativas e pode perder benefícios do INSS. Por isso, é muito importante manter os pagamentos em dia!'
    },
    {
      categoria: 'custos',
      pergunta: 'Qual o limite de faturamento do MEI em 2025?',
      resposta: 'O limite de faturamento do MEI em 2025 é de R$ 81.000,00 por ano, o que dá uma média de R$ 6.750,00 por mês. Se você ultrapassar esse limite em até 20% (R$ 97.200), paga uma guia complementar. Se ultrapassar mais de 20%, precisa migrar para ME (Microempresa) com efeito retroativo ao início do ano.'
    },

    // Nota Fiscal
    {
      categoria: 'nf',
      pergunta: 'MEI é obrigado a emitir nota fiscal?',
      resposta: 'O MEI é obrigado a emitir nota fiscal apenas quando vende para outras empresas (pessoa jurídica). Para vendas ao consumidor final (pessoa física), a emissão é opcional, mas recomendada. Emitir nota fiscal aumenta sua credibilidade, facilita o controle financeiro e serve como comprovante de renda.',
      destaque: true
    },
    {
      categoria: 'nf',
      pergunta: 'Como emitir nota fiscal sendo MEI?',
      resposta: 'Para emitir nota fiscal, o MEI precisa: 1) Se cadastrar na prefeitura (para NFS-e de serviços) ou na Secretaria da Fazenda do estado (para NF-e de produtos); 2) Obter autorização para emissão; 3) Usar um sistema emissor (como o MeuMei). Com nossa plataforma, você emite notas em poucos cliques, direto pelo celular!'
    },
    {
      categoria: 'nf',
      pergunta: 'MEI precisa de certificado digital?',
      resposta: 'Na maioria das cidades, o MEI está dispensado de certificado digital para emitir NFS-e (nota de serviço). Porém, algumas prefeituras e para emissão de NF-e (produtos), pode ser necessário. O certificado digital custa entre R$ 100 e R$ 300 por ano. Com o MeuMei, verificamos automaticamente se você precisa ou não.'
    },
    {
      categoria: 'nf',
      pergunta: 'Qual a diferença entre NFS-e, NF-e e NFC-e?',
      resposta: 'NFS-e (Nota Fiscal de Serviços Eletrônica): para prestação de serviços. NF-e (Nota Fiscal Eletrônica): para venda de produtos para outras empresas. NFC-e (Nota Fiscal ao Consumidor): para venda de produtos ao consumidor final (varejo). O tipo de nota depende da sua atividade e para quem você está vendendo.'
    },

    // Obrigações
    {
      categoria: 'obrigacoes',
      pergunta: 'Quais são as obrigações do MEI?',
      resposta: 'As obrigações do MEI são: 1) Pagar o DAS mensalmente (até dia 20); 2) Entregar a Declaração Anual (DASN-SIMEI) até 31 de maio; 3) Emitir nota fiscal quando vender para empresas; 4) Respeitar o limite de faturamento de R$ 81.000/ano; 5) Se tiver funcionário, cumprir obrigações trabalhistas. São poucas obrigações comparado a outros tipos de empresa!',
      destaque: true
    },
    {
      categoria: 'obrigacoes',
      pergunta: 'O que é DASN-SIMEI (Declaração Anual)?',
      resposta: 'DASN-SIMEI é a Declaração Anual do Simples Nacional do MEI. Nela você informa quanto faturou no ano anterior e se teve funcionário. O prazo de entrega é até 31 de maio de cada ano. Se não entregar, paga multa mínima de R$ 50,00. Com o MeuMei, geramos sua declaração automaticamente!'
    },
    {
      categoria: 'obrigacoes',
      pergunta: 'MEI precisa de contador?',
      resposta: 'Não, o MEI não é obrigado a ter contador! As obrigações são simples e podem ser feitas pelo próprio empreendedor. Porém, contar com uma plataforma como o MeuMei ajuda a manter tudo organizado: DAS em dia, declaração anual, notas fiscais e controle financeiro. Se preferir, pode contratar contador para orientações mais complexas.'
    },
    {
      categoria: 'obrigacoes',
      pergunta: 'Como dar baixa no MEI?',
      resposta: 'Para encerrar seu MEI, acesse o Portal do Empreendedor ou use o MeuMei, vá em "Baixa" e siga o passo a passo. Você precisará: quitar os DAS pendentes, entregar a Declaração de Extinção e confirmar a baixa. O processo é online e gratuito. Após a baixa, você deixa de ter obrigações como MEI.'
    },

    // Benefícios
    {
      categoria: 'beneficios',
      pergunta: 'Quais os benefícios de ser MEI?',
      resposta: 'Os principais benefícios são: 1) CNPJ para formalizar seu negócio; 2) Emissão de notas fiscais; 3) Contribuição para o INSS (aposentadoria, auxílio-doença, salário-maternidade); 4) Acesso a crédito empresarial com juros menores; 5) Possibilidade de vender para o governo; 6) Impostos simplificados em valor fixo; 7) Conta bancária empresarial; 8) Credibilidade com clientes.',
      destaque: true
    },
    {
      categoria: 'beneficios',
      pergunta: 'MEI tem direito a aposentadoria?',
      resposta: 'Sim! O MEI contribui para o INSS através do DAS (5% do salário mínimo) e tem direito a: aposentadoria por idade (65 anos homem, 62 mulher + 15/20 anos de contribuição), aposentadoria por invalidez, auxílio-doença, salário-maternidade (10 meses de carência), pensão por morte e auxílio-reclusão para dependentes.'
    },
    {
      categoria: 'beneficios',
      pergunta: 'MEI pode ter funcionário?',
      resposta: 'Sim, o MEI pode ter no máximo 1 funcionário que receba até 1 salário mínimo ou o piso da categoria. Você deve: registrar em carteira, recolher FGTS (8%) e INSS patronal (3%), fazer eSocial. O custo total com encargos fica em torno de 11% a mais do salário. Com o MeuMei, ajudamos você a gerenciar seu funcionário.'
    },
    {
      categoria: 'beneficios',
      pergunta: 'MEI pode participar de licitações?',
      resposta: 'Sim! O MEI pode participar de licitações públicas e tem preferência em compras de até R$ 80.000. Muitos órgãos públicos dão preferência para MEIs. Para participar, você precisa estar com o CNPJ regular, DAS em dia e ter o Certificado de Condição de MEI atualizado. É uma ótima oportunidade de vendas!'
    },
    {
      categoria: 'beneficios',
      pergunta: 'MEI pode ter conta no banco?',
      resposta: 'Sim! Com o CNPJ do MEI você pode abrir conta corrente empresarial (PJ). Vantagens: separar finanças pessoais das do negócio, acesso a crédito empresarial, maquininha de cartão no CNPJ, Pix empresarial. Bancos digitais como Nubank, Inter e C6 oferecem contas PJ gratuitas para MEI.'
    },
  ];

  // Filtrar FAQs
  const faqsFiltradas = faqs.filter(faq => {
    const matchCategoria = activeCategory === 'todas' || faq.categoria === activeCategory;
    const matchSearch = faq.pergunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       faq.resposta.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategoria && matchSearch;
  });

  return (
    <div className="min-h-screen font-sans bg-white">
      {/* Schema.org FAQPage */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.slice(0, 10).map(faq => ({
            "@type": "Question",
            "name": faq.pergunta,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.resposta
            }
          }))
        })
      }} />

      <Header />

      {/* Hero Section */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 50%, #0369A1 100%)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center"
            >
              <HelpCircle className="w-10 h-10" />
            </motion.div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Perguntas Frequentes<br />
              <span className="text-yellow-300">sobre MEI</span>
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed opacity-90 mb-8">
              Tire todas as suas dúvidas sobre Microempreendedor Individual. 
              Abertura, custos, nota fiscal, obrigações e muito mais!
            </p>

            {/* Barra de busca */}
            <div className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar pergunta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white text-gray-800 placeholder-gray-400 focus:ring-4 focus:ring-white/30 outline-none text-lg"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Categorias */}
          <div className="mb-10 overflow-x-auto pb-4 -mx-4 px-4">
            <div className="flex gap-2 min-w-max">
              {categorias.map((cat) => (
                <motion.button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                    activeCategory === cat.id
                      ? 'bg-sky-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  {cat.nome}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Contador de resultados */}
          <p className="text-gray-500 mb-6">
            {faqsFiltradas.length} {faqsFiltradas.length === 1 ? 'pergunta encontrada' : 'perguntas encontradas'}
          </p>

          {/* Lista de FAQs */}
          <div className="space-y-4">
            {faqsFiltradas.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`border rounded-2xl overflow-hidden ${
                  faq.destaque ? 'border-sky-300 bg-sky-50/50' : 'border-gray-200'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3 pr-4">
                    {faq.destaque && (
                      <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="font-semibold text-gray-800">{faq.pergunta}</span>
                  </div>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 md:px-6 pb-5 md:pb-6">
                        <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                          {faq.resposta}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Sem resultados */}
          {faqsFiltradas.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Nenhuma pergunta encontrada
              </h3>
              <p className="text-gray-500 mb-6">
                Tente buscar por outros termos ou selecione outra categoria.
              </p>
              <button
                onClick={() => { setSearchTerm(''); setActiveCategory('todas'); }}
                className="text-sky-600 font-medium hover:underline"
              >
                Limpar filtros
              </button>
            </div>
          )}

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-sky-500 to-blue-600 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative z-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">
                Ainda tem dúvidas?
              </h2>
              <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                Nossa equipe está pronta para te ajudar! Fale conosco pelo WhatsApp 
                ou crie sua conta e tenha suporte completo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  onClick={() => navigateTo('https://app.meumei.net/register')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white text-sky-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  <Zap className="w-5 h-5" />
                  Criar Conta Grátis
                </motion.button>
                <motion.a
                  href="https://wa.me/5511999999999?text=Olá! Tenho uma dúvida sobre MEI"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  Falar no WhatsApp
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Links para outros conteúdos */}
          <div className="mt-16">
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
              Conteúdos Relacionados
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <a 
                href="/nota-fiscal-mei"
                className="block p-6 bg-emerald-50 border border-emerald-200 rounded-2xl hover:shadow-lg transition-all group"
              >
                <FileText className="w-8 h-8 text-emerald-600 mb-3" />
                <h4 className="font-bold text-gray-800 mb-2 group-hover:text-emerald-600 transition-colors">
                  Nota Fiscal MEI
                </h4>
                <p className="text-gray-600 text-sm">
                  Aprenda a emitir notas fiscais
                </p>
              </a>
              <a 
                href="/declaracao-anual-mei"
                className="block p-6 bg-purple-50 border border-purple-200 rounded-2xl hover:shadow-lg transition-all group"
              >
                <Calculator className="w-8 h-8 text-purple-600 mb-3" />
                <h4 className="font-bold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
                  Declaração Anual
                </h4>
                <p className="text-gray-600 text-sm">
                  Guia completo da DASN-SIMEI
                </p>
              </a>
              <a 
                href="/planos"
                className="block p-6 bg-amber-50 border border-amber-200 rounded-2xl hover:shadow-lg transition-all group"
              >
                <Star className="w-8 h-8 text-amber-600 mb-3" />
                <h4 className="font-bold text-gray-800 mb-2 group-hover:text-amber-600 transition-colors">
                  Planos MeuMei
                </h4>
                <p className="text-gray-600 text-sm">
                  Conheça nossos planos
                </p>
              </a>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPageCompleta;